// ── AskAiSection ─────────────────────────────────────────────────────────────
// Coordinator that holds the per-poem AI session and paste-back state.
// Renders PoemMemoryPanel above AskAiAboutVerseCard and wires them together.
//
// pendingPasteType tracks which paste-back panel is open:
//   'verse'    — verse analysis paste-back (per bayt)
//   'overview' — poem overview paste-back (once per poem)
//   null       — no paste-back panel open
//
// Cross-device sync: on mount, if no local session exists, fetch from server.
// After every paste-back save, session is also written to the server (fire-and-forget).

import { useState, useEffect, useCallback, useRef } from 'react';
import { poemSessionManager } from '../lib/poem-session-manager.js';
import { getStorageMode } from '../lib/poem-session-db.js';
import { getQuranicAllusionScore } from '../lib/quranic-allusion-scores.js';
import { supabase } from '../lib/supabase.js';
import { lsGet } from '../lib/storage.js';
import PoemMemoryPanel from './PoemMemoryPanel.jsx';
import AskAiAboutVerseCard from './AskAI.jsx';

const API = window.location.origin;

async function getAuthHeaders() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token  = session?.access_token;
    const userId = lsGet('diwanPrismaUserId');
    if (!token || !userId) return null;
    return { token, userId, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
  } catch {
    return null;
  }
}

async function fetchSessionFromServer(poemId) {
  const auth = await getAuthHeaders();
  if (!auth) return null;
  try {
    const res = await fetch(`${API}/api/users/${auth.userId}/poem-sessions?poemId=${encodeURIComponent(poemId)}`, {
      headers: auth.headers,
    });
    if (!res.ok) return null;
    const { session } = await res.json();
    return session ?? null;
  } catch {
    return null;
  }
}

function syncSessionToServer(poemId, session) {
  getAuthHeaders().then(auth => {
    if (!auth) return;
    const hasContent = session?.runningSummary || session?.poemOverview || session?.additionalNotes;
    if (!hasContent) return;
    fetch(`${API}/api/users/${auth.userId}/poem-sessions`, {
      method:  'POST',
      headers: auth.headers,
      body:    JSON.stringify({
        poemId,
        runningSummary:  session.runningSummary  ?? '',
        verseHistory:    session.verseHistory    ?? [],
        poemOverview:    session.poemOverview    ?? '',
        additionalNotes: session.additionalNotes ?? '',
      }),
    }).catch(() => {});
  });
}

function syncDeleteToServer(poemId) {
  getAuthHeaders().then(auth => {
    if (!auth) return;
    fetch(`${API}/api/users/${auth.userId}/poem-sessions?poemId=${encodeURIComponent(poemId)}`, {
      method:  'DELETE',
      headers: auth.headers,
    }).catch(() => {});
  });
}

/**
 * @param {{
 *   poemId:        string,
 *   poem:          object,
 *   verse:         object,
 *   verseIdx:      number,
 *   displayVerses: object[],
 *   onNavigateToVerse?: (verseId: string) => void,
 *   onAIOpened?:   () => void,
 *   onBackfillDepth?: (session: object) => void,
 *   onPasteBackSaved?: () => void,
 * }} props
 */
export default function AskAiSection({ poemId, poem, verse, verseIdx, displayVerses, onNavigateToVerse, onAIOpened, onBackfillDepth, onPasteBackSaved }) {
  const [session,          setSession]          = useState(null);
  const [pendingPasteType, setPendingPasteType] = useState(null); // 'verse' | 'overview' | null
  const [memoryOnly,       setMemoryOnly]       = useState(false);

  // P0: Reset the verse paste panel when the user navigates to a different verse.
  // Overview paste persists (poem-wide) but verse paste targets currentVerseId — stale after nav.
  useEffect(() => {
    setPendingPasteType(prev => prev === 'verse' ? null : prev);
  }, [verseIdx]);
  const syncedFromServer = useRef(false);
  const backfilled = useRef(false);

  // Load session on mount: try local first, fall back to server if empty.
  useEffect(() => {
    if (!poemId) return;
    let cancelled = false;

    poemSessionManager.getSession(poemId).then(async local => {
      if (cancelled) return;
      setMemoryOnly(getStorageMode() === 'mem');

      const hasLocal = local?.runningSummary || local?.poemOverview;
      if (hasLocal) {
        setSession(local);
        syncedFromServer.current = true;
        if (!backfilled.current && onBackfillDepth) {
          backfilled.current = true;
          onBackfillDepth(local);
        }
        return;
      }

      if (!syncedFromServer.current) {
        syncedFromServer.current = true;
        const remote = await fetchSessionFromServer(poemId);
        if (cancelled || (!remote?.runningSummary && !remote?.poemOverview)) return;
        await poemSessionManager.saveSession({
          poemId:          remote.poemId,
          runningSummary:  remote.runningSummary  ?? '',
          verseHistory:    Array.isArray(remote.verseHistory) ? remote.verseHistory : [],
          poemOverview:    remote.poemOverview    ?? '',
          additionalNotes: remote.additionalNotes ?? '',
          lastVerseId:     remote.verseHistory?.at(-1)?.verseId ?? null,
          lastUpdatedAt:   remote.lastUpdatedAt ?? Date.now(),
        });
        if (!cancelled) {
          setSession(remote);
          if (!backfilled.current && onBackfillDepth) {
            backfilled.current = true;
            onBackfillDepth(remote);
          }
        }
      }
    });

    return () => { cancelled = true; };
  }, [poemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSessionChange = useCallback((updated) => {
    setSession(updated);
    if (updated?.runningSummary || updated?.poemOverview || updated?.additionalNotes) {
      syncSessionToServer(poemId, updated);
    } else {
      syncDeleteToServer(poemId);
    }
  }, [poemId]);

  const handlePendingPaste = useCallback((type) => setPendingPasteType(type), []);
  const handlePasteDismiss = useCallback(() => { setPendingPasteType(null); onPasteBackSaved?.(); }, [onPasteBackSaved]);

  if (!verse) return null;

  const verseId              = verse.id ?? `${poemId}_v${verseIdx}`;
  const previousVerseText    = displayVerses[verseIdx - 1]?.textAr ?? '';
  const nextVerseText        = displayVerses[verseIdx + 1]?.textAr ?? '';
  const quranicAllusionScore = getQuranicAllusionScore(poem.poetAr) / 10;
  // Number each bayt so the AI can unambiguously reference "Bayt 1", "Bayt 2"...
  // Plain join with \n is ambiguous because bayts can contain internal caesura newlines.
  const allVersesText = displayVerses
    .map((v, i) => `${i + 1}. ${v.textAr ?? v.text ?? ''}`)
    .filter(Boolean)
    .join('\n');

  return (
    <div style={{ padding: '0 1rem 1rem' }}>
      {memoryOnly && (
        <div style={{
          marginBottom: '0.75rem', padding: '0.55rem 0.75rem',
          background: 'rgba(212,168,83,0.07)',
          border: '1px solid rgba(212,168,83,0.3)',
          borderRadius: 4, fontSize: '0.64rem',
          color: 'var(--text3)', lineHeight: 1.5,
          fontFamily: "'Josefin Sans', sans-serif",
        }}>
          Storage unavailable — poem memory will not persist after you close this tab.
        </div>
      )}
      <PoemMemoryPanel
        poemId={poemId}
        currentVerseId={verseId}
        pendingPasteType={pendingPasteType}
        session={session}
        displayVerses={displayVerses}
        onSessionChange={handleSessionChange}
        onSaved={handlePasteDismiss}
        onNavigateToVerse={onNavigateToVerse}
        onRequestPaste={handlePendingPaste}
      />
      <AskAiAboutVerseCard
        poemId={poemId}
        verseId={verseId}
        verseText={verse.textAr}
        poemTitle={poem.titleAr}
        poetName={poem.poetAr}
        era={poem.era}
        meter={poem.meter}
        theme={poem.topic}
        quranicAllusionScore={quranicAllusionScore}
        previousVerseText={previousVerseText}
        nextVerseText={nextVerseText}
        runningSummary={session?.runningSummary ?? ''}
        poemOverview={session?.poemOverview ?? ''}
        allVersesText={allVersesText}
        historicalContext={poem.bayanExplanation ?? ''}
        verseIdx={verseIdx}
        totalVerses={displayVerses.length}
        onPendingPaste={handlePendingPaste}
        onOpen={onAIOpened}
      />
    </div>
  );
}
